/**
 * Merges multiple objects into a single object, retaining only non-undefined properties.
 *
 * @template T - The type of the objects being merged.
 * @param {...T} propsArray - An array of objects to merge.
 * @returns {T} - A new object containing the merged properties.
 */
export const mergeProps = <T extends object>(...propsArray: T[]): T => {
  // Use Partial<T> to start with an empty object
  const mergedProps: Partial<T> = {};

  for (const props of propsArray) {
    for (const key in props) {
      if (
        Object.prototype.hasOwnProperty.call(props, key) &&
        props[key] !== undefined
      ) {
        mergedProps[key] = props[key];
      }
    }
  }

  // Use type assertion to cast back to type T
  return mergedProps as T;
};

export const mergePropsWithInitials = <T extends TV, TV extends object>(
  object1: T | Partial<T> | undefined,
  object2: TV,
): TV => {
  if (!object1 && !!object2) return object2;
  if (!!object1 && !object2) return object1 as unknown as TV;

  if (object1 && object2) {
    const temp = { ...object2 };
    const keys = Object.keys(object2);

    if (keys)
      for (const key of keys) {
        // @ts-expect-error key can be not exist in temp
        temp[key] =
          object1[key as keyof typeof object1] ??
          object2[key as keyof typeof object2];
      }

    return temp;
  }

  return (object1 as unknown as TV) ?? object2;
};
